import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { cx } from './cx.js';
import { Icon } from './icon.js';

/**
 * One task on top of the page (Dialog README): native `<dialog>` with
 * `showModal()`, so focus containment, Escape and the backdrop come from the browser.
 * Footer order: Cancel first, then the action named by its effect.
 */
export function Dialog({
  open,
  title,
  subtitle,
  onClose,
  footer,
  children,
}: {
  open: boolean;
  title: string;
  subtitle?: ReactNode;
  onClose: () => void;
  footer?: ReactNode;
  children?: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  // Unique per dialog: several dialogs can share a page.
  const titleId = useId();
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      // Without showModal (older jsdom) the open attribute still shows the dialog.
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
    }
    if (!open && dialog.open) {
      if (typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
    }
  }, [open]);
  return (
    <dialog
      ref={ref}
      className="dialog"
      aria-labelledby={titleId}
      onClose={onClose}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
    >
      <div className="dlg-h">
        <h2 id={titleId}>{title}</h2>
        {subtitle && <p className="sub">{subtitle}</p>}
      </div>
      {children && <div className="dlg-b">{children}</div>}
      {footer && <div className="dlg-f">{footer}</div>}
    </dialog>
  );
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Slides in from a side (the phone sidebar, a details panel). Focus stays
 * inside while it is open (WAI-ARIA dialog), the rest of the page is inert,
 * Escape and the scrim close it, and focus returns to where it was.
 */
export function Drawer({
  open,
  label,
  side = 'left',
  onClose,
  initialFocus,
  className,
  children,
}: {
  open: boolean;
  label: string;
  /** `bottom` (D56): a sheet rising from the foot of the screen, with a grab handle. */
  side?: 'left' | 'right' | 'bottom';
  onClose: () => void;
  /** A selector inside the panel to focus on open (a chat's message box); the first control otherwise. */
  initialFocus?: string;
  /** A design-system modifier for the panel: `wide` (560 px, D57). */
  className?: 'wide';
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  // Callers pass a fresh onClose on every render; the focus effect must not re-run for that.
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    const panel = ref.current;
    const wanted = initialFocus ? panel?.querySelector<HTMLElement>(initialFocus) : null;
    const first = wanted ?? panel?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? panel)?.focus();
    const siblings = Array.from(document.body.children).filter(
      (el) => el !== panel && el !== panel?.previousElementSibling && !el.contains(panel),
    );
    for (const el of siblings) el.setAttribute('inert', '');
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') return close.current();
      if (e.key !== 'Tab' || !panel) return;
      const items = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (items.length === 0) return;
      const firstItem = items[0] as HTMLElement;
      const lastItem = items[items.length - 1] as HTMLElement;
      if (e.shiftKey && document.activeElement === firstItem) {
        e.preventDefault();
        lastItem.focus();
      } else if (!e.shiftKey && document.activeElement === lastItem) {
        e.preventDefault();
        firstItem.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      for (const el of siblings) el.removeAttribute('inert');
      previous?.focus?.();
    };
  }, [open]);
  if (!open) return null;
  return (
    <>
      <div className="drawer-scrim" onClick={onClose} aria-hidden="true" />
      <div
        ref={ref}
        className={cx('drawer', side !== 'left' && side, className)}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        tabIndex={-1}
      >
        {side === 'bottom' && <span className="sheet-grab" aria-hidden="true" />}
        {children}
      </div>
    </>
  );
}

export type ToastKind = 'status' | 'error';

export interface ToastOptions {
  kind?: ToastKind;
  /** An action such as Undo. A toast with an action stays until dismissed or used. */
  action?: { label: string; onAction: () => void };
  /** Milliseconds; ignored when there is an action. */
  duration?: number;
}

interface ToastItem extends ToastOptions {
  id: number;
  text: string;
}

export type ShowToast = (text: string, options?: ToastOptions) => void;

const ToastContext = createContext<ShowToast>(() => undefined);

/**
 * Toasts say what happened and what follows (ADR 126, D16). Confirmations are
 * announced politely, errors assertively, and neither moves focus. One at a
 * time per kind (ADR 248): a new one replaces the last. A toast with an Undo stays
 * for its `duration` (the undo window), or until closed when it has none.
 */
export function ToastProvider({
  children,
  duration = 5000,
}: {
  children: ReactNode;
  duration?: number;
}) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const next = useRef(0);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());
  const remove = useCallback((id: number) => {
    const t = timers.current.get(id);
    if (t) clearTimeout(t);
    timers.current.delete(id);
    setItems((all) => all.filter((x) => x.id !== id));
  }, []);
  // A toast still showing when the provider unmounts must not fire later.
  useEffect(() => {
    const all = timers.current;
    return () => {
      all.forEach(clearTimeout);
      all.clear();
    };
  }, []);
  const arm = useCallback(
    (item: ToastItem) => {
      // ADR 248 (finding 006): a toast with an action leaves too, when its window (Undo) ends.
      if (item.action && item.duration === undefined) return;
      timers.current.set(
        item.id,
        setTimeout(() => remove(item.id), item.duration ?? duration),
      );
    },
    [duration, remove],
  );
  const show = useCallback<ShowToast>(
    (text, options) => {
      const item: ToastItem = { id: ++next.current, text, ...options };
      // ADR 248 (finding 006): one toast of each kind at a time; a new one replaces the last, so
      // several quick changes never stack over the page.
      const kind = item.kind === 'error' ? 'error' : 'status';
      setItems((all) => {
        for (const old of all)
          if ((old.kind === 'error' ? 'error' : 'status') === kind) {
            const t = timers.current.get(old.id);
            if (t) clearTimeout(t);
            timers.current.delete(old.id);
          }
        return [...all.filter((x) => (x.kind === 'error' ? 'error' : 'status') !== kind), item];
      });
      arm(item);
    },
    [arm],
  );
  const pause = (id: number) => {
    const t = timers.current.get(id);
    if (t) clearTimeout(t);
  };
  const resume = (id: number) => {
    const item = items.find((x) => x.id === id);
    if (item) arm(item);
  };
  const statuses = items.filter((t) => t.kind !== 'error');
  const errors = items.filter((t) => t.kind === 'error');
  const render = (t: ToastItem) => (
    <div
      className={cx('toast', t.kind === 'error' && 'error')}
      role={t.kind === 'error' ? 'alert' : undefined}
      key={t.id}
      onMouseEnter={() => pause(t.id)}
      onMouseLeave={() => resume(t.id)}
    >
      <span>{t.text}</span>
      {t.action && (
        <button
          type="button"
          className="toast-action"
          onClick={() => {
            remove(t.id);
            t.action?.onAction();
          }}
        >
          {t.action.label}
        </button>
      )}
      <button
        type="button"
        className="toast-close"
        aria-label="Close notification"
        onClick={() => remove(t.id)}
      >
        <Icon name="close" size={16} />
      </button>
    </div>
  );
  return (
    <ToastContext.Provider value={show}>
      {children}
      <div className="toasts">
        <div role="status" aria-live="polite">
          {statuses.map(render)}
        </div>
        {/* Assertive region without a role: each error toast is its own alert, so an
            empty region never counts as one on the page. */}
        <div aria-live="assertive" aria-atomic="true">
          {errors.map(render)}
        </div>
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ShowToast {
  return useContext(ToastContext);
}
